import React, { FC } from 'react'

import { Typography, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import { CircularProgressbar } from 'react-circular-progressbar'
import { useTranslation } from 'react-i18next'

import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { toggleBigWidget } from '../DashboardSlice'
import { MoneyValue } from '../MoneyValue'
import { BigWidgetsName } from '../types'
import { WidgetWrapper } from '../widgetsComponents'

export const ProfitFactor: FC = () => {
  const { t } = useTranslation()
  const { spacing, palette } = useTheme()
  const dispatch = useAppDispatch()

  const { widgetsData, bigWidgetsList } = useAppSelector(({ dashboard }) => dashboard)
  const { currentAccount } = useAppSelector(({ userAccounts }) => userAccounts)

  const successProfit = widgetsData?.profitFactor?.grossProfit
    ? Number(widgetsData.profitFactor.grossProfit)
    : 0
  const lossProfit = widgetsData?.profitFactor?.grossLoss
    ? Number(widgetsData.profitFactor.grossLoss)
    : 0
  const currency = currentAccount?.currency as string

  const profit = widgetsData?.profitFactor?.factor || '-'

  const percentage = (successProfit / (successProfit + Math.abs(lossProfit))) * 100

  const handleCheck = () => {
    dispatch(toggleBigWidget(BigWidgetsName.profitFactor))
  }

  return (
    <WidgetWrapper
      label={t('profitFactor', 'Profit factor')}
      handleCheck={handleCheck}
      isChecked={bigWidgetsList[BigWidgetsName.profitFactor]}
      explanation={t(
        'measuresTheRatioOfMoneyEarnedToMoneyLostInTrading',
        'Measures the ratio of money earned to money lost in trading',
      )}
    >
      <Box display="flex" alignItems="center" height="100%">
        <Box width={spacing(15.5)} height={spacing(15.5)}>
          <CircularProgressbar
            value={isNaN(percentage) ? 0 : percentage}
            text={profit.toString()}
            styles={{
              path: {
                stroke: palette.green[400],
                strokeLinecap: 'butt',
                transition: 'stroke-dashoffset 0.5s ease 0s',
                transformOrigin: 'center center',
              },
              trail: {
                stroke: isNaN(percentage) ? palette.grey[500] : palette.red[400],
                strokeLinecap: 'butt',
                transformOrigin: 'center center',
              },
              text: {
                fill: palette.text.primary,
                fontSize: spacing(3),
                fontWeight: 'bold',
                fontFamily: 'Wix Madefor Display',
                textAnchor: 'middle',
                transform: 'translate(0, 6px)',
              },
              background: {
                fill: palette.cyan[500],
              },
            }}
            strokeWidth={6}
          />
        </Box>
        <Box flex={1} display="flex" justifyContent="center" alignItems="center">
          <Box
            width="auto"
            display="flex"
            flexDirection="column"
            gap={spacing(1.25)}
            justifyContent="center"
            alignItems="flex-start"
          >
            <Box>
              <Typography fontWeight="bold" color={palette.grey[700]} fontSize={12}>
                {t('grossProfits', 'Gross profits')}
              </Typography>
              <MoneyValue money={successProfit} currency={currency} />
            </Box>
            <Box>
              <Typography fontWeight="bold" color={palette.grey[700]} fontSize={12}>
                {t('grossLoss', 'Gross loss')}
              </Typography>
              <MoneyValue money={lossProfit} currency={currency} />
            </Box>
          </Box>
        </Box>
      </Box>
    </WidgetWrapper>
  )
}
